/**
 * cardFeatureDetection.js
 *
 * Unified card detection system for config-driven architecture.
 * Replaces hardcoded normalizedLoadout.includes() checks throughout the codebase.
 *
 * Part of Issue #4 Architecture Refactor (Phase 11G)
 * @see docs/technical/issue4-architecture-refactor.md
 */

/**
 * Normalize a card ID for comparison
 * Handles various card ID formats:
 * - "core_domain_card_armorer" -> "armorer"
 * - "Armorer" -> "armorer"
 * - "bare bones" -> "bare-bones"
 *
 * @param {string} cardId - Card ID to normalize
 * @returns {string} Normalized card key
 */
export const normalizeCardId = (cardId) => {
  if (!cardId) return '';

  return cardId
    .replace(/^core_domain_card_/, '')
    .toLowerCase()
    .replace(/[\s_]+/g, '-');
};

/**
 * Get normalized loadout array from character
 * @param {Object} character - Character object
 * @returns {string[]} Array of normalized card keys
 */
export const getNormalizedLoadout = (character) => {
  if (!character?.loadout) return [];

  return character.loadout.map(normalizeCardId);
};

/**
 * Check if character has a specific domain card in their loadout
 * @param {Object} character - Character object
 * @param {string} cardKey - Card key to check (e.g., 'armorer', 'bare-bones')
 * @returns {boolean}
 */
export const hasLoadoutCard = (character, cardKey) => {
  if (!character || !cardKey) return false;

  const normalizedLoadout = getNormalizedLoadout(character);
  const normalizedKey = normalizeCardId(cardKey);

  return normalizedLoadout.includes(normalizedKey);
};

/**
 * Check if character has any of the specified domain cards
 * @param {Object} character - Character object
 * @param {string[]} cardKeys - Array of card keys to check
 * @returns {boolean}
 */
export const hasAnyLoadoutCard = (character, cardKeys) => {
  if (!character || !cardKeys?.length) return false;

  return cardKeys.some(key => hasLoadoutCard(character, key));
};

/**
 * Get all matching cards from a config object
 * @param {Object} character - Character object
 * @param {Object} configMap - Config object keyed by card key
 * @returns {Array<{cardKey: string, config: Object}>}
 */
export const getActiveCards = (character, configMap) => {
  if (!character || !configMap) return [];

  const results = [];

  for (const [cardKey, config] of Object.entries(configMap)) {
    if (hasLoadoutCard(character, cardKey)) {
      results.push({ cardKey, config });
    }
  }

  return results;
};

/**
 * Find a card config that provides a specific capability
 * @param {Object} character - Character object
 * @param {string} capability - Capability to look for (e.g., 'armorBonus', 'damageBonus')
 * @param {Object} configMap - Config object to search
 * @returns {Object|null} - { cardKey: string, config: Object } or null
 */
export const getCardWithCapability = (character, capability, configMap) => {
  if (!character || !capability || !configMap) return null;

  for (const [cardKey, config] of Object.entries(configMap)) {
    // Check if config has the capability
    if (config[capability] === undefined) continue;

    // Check if card is in loadout
    if (!hasLoadoutCard(character, cardKey)) continue;

    return { cardKey, config };
  }

  return null;
};
